/**
 * KITCHNEST interactions — progressive enhancement only (everything works without it).
 *  - [data-kn-reveal]   fade/rise into view (IntersectionObserver)
 *  - [data-kn-tilt]     3D tilt + glare following the pointer (fine pointers only)
 *  - [data-kn-spot]     spotlight position for gold-glow hover effects
 *  - [data-kn-tilt-scope] 3D logo emblem follows the pointer across its section
 *  - [data-kn-parallax] pointer parallax inside the hero
 *  - <kn-faq-tabs>      topic filters for the FAQ
 * Respects prefers-reduced-motion and never blocks scrolling or taps.
 */
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const motionOK = () => !reduceMotion.matches;

/* ---------- Reveal on scroll ----------
   Content is only ever hidden after this script has run (html.kn-reveal-ready), so if the
   script fails to load on a device, every section stays fully visible. Anything already on
   screen is marked visible before hiding is switched on, so nothing flickers. */
function initReveal(root = document) {
  const items = root.querySelectorAll('[data-kn-reveal]:not(.is-in)');
  if (!items.length) return;
  if (!motionOK() || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  const viewportH = window.innerHeight || document.documentElement.clientHeight;
  items.forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top < viewportH && r.bottom > 0) el.classList.add('is-in');
  });
  document.documentElement.classList.add('kn-reveal-ready');
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  items.forEach((el) => {
    if (!el.classList.contains('is-in')) io.observe(el);
  });
  // Safety net: never leave content hidden.
  setTimeout(() => items.forEach((el) => el.classList.add('is-in')), 4000);
}

/* ---------- Tilt + glare + spotlight ---------- */
function bindTilt(el, max = 7) {
  if (el.dataset.knTiltBound) return;
  el.dataset.knTiltBound = '1';
  let frame = 0;
  const onMove = (event) => {
    const rect = el.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      el.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
      el.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
      if (el.hasAttribute('data-kn-tilt') && motionOK()) {
        el.style.setProperty('--ry', `${((x - 0.5) * max * 2).toFixed(2)}deg`);
        el.style.setProperty('--rx', `${((0.5 - y) * max * 2).toFixed(2)}deg`);
        el.classList.add('is-tilting');
      }
    });
  };
  const onLeave = () => {
    cancelAnimationFrame(frame);
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
    el.classList.remove('is-tilting');
  };
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerleave', onLeave);
}

function initTilt(root = document) {
  if (!finePointer.matches) return;
  root.querySelectorAll('[data-kn-tilt], [data-kn-spot]').forEach((el) => bindTilt(el));
}

/* ---------- 3D emblem follows pointer within its section ---------- */
function initEmblems(root = document) {
  root.querySelectorAll('[data-kn-tilt-scope]').forEach((emblem) => {
    if (emblem.dataset.knBound) return;
    emblem.dataset.knBound = '1';
    const stage = emblem.querySelector('[data-kn-tilt-target]');
    const scope = emblem.closest('section') || emblem;
    if (!stage) return;
    let frame = 0;
    let active = false;

    // Gentle idle rotation when the pointer is not over the section
    const idle = (t) => {
      if (!active && motionOK()) {
        const ry = Math.sin(t / 2400) * 14;
        const rx = Math.cos(t / 3100) * 5;
        stage.style.setProperty('--ery', `${ry.toFixed(2)}deg`);
        stage.style.setProperty('--erx', `${rx.toFixed(2)}deg`);
      }
      frame = requestAnimationFrame(idle);
    };

    const visibility = new IntersectionObserver(([entry]) => {
      cancelAnimationFrame(frame);
      if (entry.isIntersecting && motionOK()) frame = requestAnimationFrame(idle);
    });
    visibility.observe(emblem);

    if (finePointer.matches) {
      scope.addEventListener('pointermove', (event) => {
        if (!motionOK()) return;
        active = true;
        const rect = emblem.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = Math.max(-1, Math.min(1, (event.clientX - cx) / (window.innerWidth / 2)));
        const dy = Math.max(-1, Math.min(1, (event.clientY - cy) / (window.innerHeight / 2)));
        stage.style.setProperty('--ery', `${(dx * 28).toFixed(2)}deg`);
        stage.style.setProperty('--erx', `${(-dy * 18).toFixed(2)}deg`);
      });
      scope.addEventListener('pointerleave', () => {
        active = false;
      });
    }
  });
}

/* ---------- Hero pointer parallax ---------- */
function initParallax(root = document) {
  if (!finePointer.matches) return;
  root.querySelectorAll('[data-kn-hero]').forEach((hero) => {
    if (hero.dataset.knBound) return;
    hero.dataset.knBound = '1';
    const layers = [...hero.querySelectorAll('[data-kn-parallax]')];
    let frame = 0;
    hero.addEventListener('pointermove', (event) => {
      if (!motionOK()) return;
      const rect = hero.getBoundingClientRect();
      const dx = (event.clientX - rect.left) / rect.width - 0.5;
      const dy = (event.clientY - rect.top) / rect.height - 0.5;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        for (const layer of layers) {
          const depth = Number(layer.getAttribute('data-kn-parallax')) || 0.05;
          layer.style.setProperty('--px', `${(dx * depth * 400).toFixed(1)}px`);
          layer.style.setProperty('--py', `${(dy * depth * 400).toFixed(1)}px`);
        }
      });
    });
    hero.addEventListener('pointerleave', () => {
      for (const layer of layers) {
        layer.style.setProperty('--px', '0px');
        layer.style.setProperty('--py', '0px');
      }
    });
  });
}

/* ---------- FAQ topic filters ---------- */
class KnFaqTabs extends HTMLElement {
  connectedCallback() {
    this.group = this.querySelector('.kn-faq__tabs');
    if (!this.group) return;
    this.group.hidden = false;
    this.buttons = [...this.group.querySelectorAll('button[data-topic]')];
    this.rows = [...this.querySelectorAll('.kn-acc__row[data-topic]')];
    this.empty = this.querySelector('.kn-faq__empty');
    this.group.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('button[data-topic]') : null;
      if (button) this.select(button);
    });
    this.group.addEventListener('keydown', (event) => {
      const index = this.buttons.indexOf(document.activeElement);
      if (index < 0) return;
      let next = -1;
      if (event.key === 'ArrowRight') next = (index + 1) % this.buttons.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.buttons.length) % this.buttons.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.buttons.length - 1;
      if (next >= 0) {
        event.preventDefault();
        this.buttons[next].focus();
        this.select(this.buttons[next]);
      }
    });
  }

  select(button) {
    const topic = button.dataset.topic;
    this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    let visible = 0;
    for (const row of this.rows) {
      const show = topic === 'all' || row.dataset.topic === topic;
      row.hidden = !show;
      if (show) visible++;
      else row.open = false;
    }
    if (this.empty) this.empty.hidden = visible > 0;
    button.scrollIntoView({ block: 'nearest', inline: 'center', behavior: motionOK() ? 'smooth' : 'auto' });
  }
}
if (!customElements.get('kn-faq-tabs')) customElements.define('kn-faq-tabs', KnFaqTabs);

/* ---------- Boot (and re-boot when the theme editor reloads a section) ---------- */
function boot(root = document) {
  initReveal(root);
  initTilt(root);
  initEmblems(root);
  initParallax(root);
}

boot();
document.addEventListener('shopify:section:load', (event) => boot(event.target));
reduceMotion.addEventListener?.('change', () => boot());
